/* sourceIndex.js
 *
 * Single source of truth for the BuildPilot Source Index page at /sources.
 *
 * Lists every external regulatory and primary-source citation referenced
 * across BuildPilot's editorial content, grouped by issuing authority.
 *
 * Why this exists (SEO + legal posture):
 *   1. E-E-A-T transparency. Google's Search Quality rater guidelines
 *      treat sites that publish an explicit "we cite these authorities"
 *      hub as more trustworthy than sites that only embed citations.
 *   2. AI Overview citation magnet. ChatGPT, Perplexity, Claude and
 *      Gemini grounding crawls prefer one URL that maps the editorial
 *      chain back to authoritative sources.
 *   3. Copyright posture. Makes explicit that BuildPilot quotes
 *      regulations and government data, and contributes independent
 *      editorial interpretation on top, not the other way around.
 *   4. Reusable. Refreshed quarterly without touching the rendered page.
 *
 * Sister file to /data/seoInventory.js (which powers /sitemap).
 *
 * Each entry:
 *   name       - the authority's full name
 *   url        - the canonical homepage or topic landing page
 *   summary    - one-sentence editorial description of what they do
 *   citedFor   - list of topics BuildPilot cites them on (machine-readable)
 *   internalLinks - BuildPilot pages where this authority is cited
 *   jurisdiction - federal / state / industry / standards
 *   lastVerified - YYYY-MM-DD the URL and remit were last re-checked
 */

export const SOURCE_INDEX = {
  'Federal Government': [
    {
      name: 'Housing Australia (Home Guarantee Scheme)',
      url: 'https://www.housingaustralia.gov.au/support-buy-home/home-guarantee-scheme',
      summary: 'Federal agency administering the 5% Deposit Scheme (formerly First Home Guarantee), Regional First Home Buyer Guarantee and Family Home Guarantee, the federal deposit-assistance programs first home buyers can use alongside SA state grants.',
      citedFor: [
        'First Home Guarantee (5% Deposit Scheme)',
        'Regional First Home Buyer Guarantee',
        'Family Home Guarantee',
        'Eligibility income caps and property thresholds',
      ],
      internalLinks: [
        { name: 'First Home Buyer Adelaide guide', href: '/first-home-buyer-adelaide' },
        { name: 'Begin Planning lifecycle pillar', href: '/lifecycle/begin-planning' },
        { name: 'House & Land Packages Adelaide', href: '/house-and-land-adelaide' },
      ],
      buildpilotTake:
        'Our reading: the $125K single / $200K couple income caps rule out a meaningful slice of dual-income Adelaide buyers who would otherwise qualify on deposit alone. The Regional First Home Buyer Guarantee is the most underused option we see, it covers Mount Barker, Murray Bridge and Strathalbyn with higher property caps than the metro scheme.',
      jurisdiction: 'federal',
      lastVerified: '2026-03-01',
    },
    {
      name: 'Australian Building Codes Board (ABCB)',
      url: 'https://www.abcb.gov.au',
      summary: 'Joint federal, state and territory body that develops and publishes the National Construction Code (NCC), the legally binding minimum standard every new home in Australia must meet.',
      citedFor: [
        'National Construction Code (NCC) 2025 edition',
        '7-star NatHERS minimum (energy efficiency)',
        'Accessibility, fire safety, plumbing and structural standards',
      ],
      internalLinks: [
        { name: 'NCC terminology entry', href: '/terminology/ncc-national-construction-code' },
        { name: 'Energy-efficient home guide', href: '/energy-efficient-home-guide' },
      ],
      buildpilotTake:
        'The 7-star NatHERS minimum that came in May 2024 added an estimated $5,000-$15,000 to a typical Adelaide build, mostly in glazing, insulation and orientation work. In our reading the up-front sticker shock surprises buyers because most builders had not refreshed their base inclusions to absorb it through 2024-2025.',
      jurisdiction: 'federal',
      lastVerified: '2026-03-01',
    },
    {
      name: 'Australian Taxation Office (ATO)',
      url: 'https://www.ato.gov.au',
      summary: 'Federal tax authority. BuildPilot references the ATO for GST treatment on new builds, capital gains exemptions on main residence, and investment-property depreciation rules relevant to house and land buyers.',
      citedFor: [
        'GST on new builds',
        'Capital gains main residence exemption',
        'Investment property depreciation (Division 43)',
      ],
      internalLinks: [
        { name: 'House & Land Packages Adelaide', href: '/house-and-land-adelaide' },
      ],
      buildpilotTake:
        'The main residence CGT exemption is the under-discussed reason a knockdown rebuild often beats sell-and-buy for Adelaide families upsizing on the same block. Sell-and-buy triggers stamp duty twice and may trigger CGT on any investment uplift, KDR avoids both.',
      jurisdiction: 'federal',
      lastVerified: '2026-03-01',
    },
  ],

  'South Australian Government': [
    {
      name: 'RevenueSA',
      url: 'https://www.revenuesa.sa.gov.au',
      summary: 'The South Australian Government tax authority. BuildPilot cites RevenueSA for First Home Owner Grant amounts, stamp duty exemption thresholds, and conveyance duty calculations on Adelaide land purchases.',
      citedFor: [
        'First Home Owner Grant (FHOG) amount and eligibility',
        'Stamp duty exemption thresholds for new builds',
        'Conveyance duty calculator inputs',
        'Foreign-buyer surcharge rules',
      ],
      internalLinks: [
        { name: 'First Home Buyer Adelaide', href: '/first-home-buyer-adelaide' },
        { name: 'Begin Planning lifecycle pillar', href: '/lifecycle/begin-planning' },
        { name: 'Adelaide Build Cost Report 2026', href: '/adelaide-build-cost-report-2026' },
      ],
      buildpilotTake:
        'The $15,000 First Home Owner Grant matters more on builds under $650,000, where it covers your conveyancer plus most of the first progress payment. On builds above $850,000 the FHOG is a rounding error in our reading, and the stamp duty bracket changes matter far more, easy mistake to chase the smaller number.',
      jurisdiction: 'state',
      lastVerified: '2026-03-01',
    },
    {
      name: 'Consumer and Business Services South Australia (CBS-SA)',
      url: 'https://www.cbs.sa.gov.au',
      summary: 'The South Australian Government regulator for builders, real estate agents and consumer-facing trades. BuildPilot cites CBS-SA for the Builder Licence Register, Building Indemnity Insurance (under the Building Work Contractors Act 1995), and the formal builder-dispute resolution process.',
      citedFor: [
        'Builder Work Contractor licence register',
        'Building Indemnity Insurance (Building Work Contractors Act 1995)',
        'Resolving Building Disputes process',
        'Consumer protection on residential building contracts',
      ],
      internalLinks: [
        { name: 'Make Choices lifecycle pillar', href: '/lifecycle/make-choices' },
        { name: 'Get Building lifecycle pillar', href: '/lifecycle/get-building' },
        { name: 'Building disputes Adelaide', href: '/building-disputes' },
        { name: 'Builder Licence Check tool', href: '/workspace/tools/builder-licence-check' },
        { name: 'Building Insurance Check tool', href: '/workspace/tools/building-insurance-check' },
      ],
      buildpilotTake:
        'The CBS Builder Licence Register is the best 60-second sanity check before signing anything. In our work with Adelaide families we still occasionally see builders advertising under a trading name that does not match the licensed entity, the register catches that in one click.',
      jurisdiction: 'state',
      lastVerified: '2026-03-01',
    },
    {
      name: 'HomeStart Finance',
      url: 'https://www.homestart.com.au',
      summary: 'South Australian Government-backed lender offering Graduate Loan, Starter Loan, Shared Equity Option and Low Deposit Loan products. BuildPilot cites HomeStart for SA-specific finance options first home buyers can use when they have a smaller deposit.',
      citedFor: [
        'Graduate Loan eligibility',
        'Starter Loan (low deposit)',
        'Shared Equity Option',
        'HomeStart Family Pledge',
      ],
      internalLinks: [
        { name: 'First Home Buyer Adelaide', href: '/first-home-buyer-adelaide' },
        { name: 'Begin Planning lifecycle pillar', href: '/lifecycle/begin-planning' },
      ],
      buildpilotTake:
        'HomeStart is underused by Adelaide buyers because the products are SA-specific and nationwide comparison sites do not surface them well. In our reading the Shared Equity Option is materially better than the federal 5% Deposit Scheme for buyers under $115,000 household income, but it cuts off above that threshold.',
      jurisdiction: 'state',
      lastVerified: '2026-03-01',
    },
    {
      name: 'SA Legislation Portal',
      url: 'https://www.legislation.sa.gov.au',
      summary: 'The official register of South Australian Acts and Regulations. BuildPilot cites the SA Legislation Portal as the primary source for the Building Work Contractors Act 1995 (SA) and related regulations governing residential building contracts in SA.',
      citedFor: [
        'Building Work Contractors Act 1995 (SA)',
        'Building Work Contractors Regulations 2011 (SA)',
        'Land and Business (Sale and Conveyancing) Act 1994 (SA)',
        'Statutory deposit caps and progress payment provisions',
      ],
      internalLinks: [
        { name: 'Make Choices lifecycle pillar', href: '/lifecycle/make-choices' },
        { name: 'Get Building lifecycle pillar', href: '/lifecycle/get-building' },
        { name: 'Love Living lifecycle pillar', href: '/lifecycle/love-living' },
      ],
      buildpilotTake:
        'The Building Work Contractors Act 1995 (SA) is the single most useful piece of legislation for Adelaide buyers to skim before signing a contract. In our experience disputes most often start in two places, the 5% deposit cap (Section 11) and the progress payment provisions, both worth a literal read.',
      jurisdiction: 'state',
      lastVerified: '2026-03-01',
    },
    {
      name: 'Government of South Australia (sa.gov.au)',
      url: 'https://www.sa.gov.au',
      summary: 'The South Australian Government services portal. BuildPilot cites sa.gov.au for energy and environment rebates, planning and council processes, and the SA Spatial Information Services map data referenced in suburb growth-trajectory analysis.',
      citedFor: [
        'SA home energy schemes and rebates',
        'Planning Portal (development applications)',
        'SA Spatial Information Services (block-level data)',
      ],
      internalLinks: [
        { name: 'Love Living lifecycle pillar', href: '/lifecycle/love-living' },
        { name: 'Government Resources workspace tool', href: '/workspace/tools/government-resources' },
      ],
      buildpilotTake:
        'The SA energy rebates page changes faster than any other regulator surface we cite, worth re-checking each quarter. Solar Schools and battery rebates often overlap with new-build incentives that builders do not always volunteer, particularly on house and land packages.',
      jurisdiction: 'state',
      lastVerified: '2026-03-01',
    },
    {
      name: 'South Australian Country Fire Service (CFS)',
      url: 'https://www.cfs.sa.gov.au',
      summary: 'The South Australian Government bushfire authority. BuildPilot cites the CFS Bushfire Hazard Mapping when explaining BAL (Bushfire Attack Level) ratings on Adelaide Hills blocks, where they are mandatory before development application.',
      citedFor: [
        'Bushfire Hazard Mapping (Adelaide Hills)',
        'BAL determination guidance',
      ],
      internalLinks: [
        { name: 'BAL Rating terminology entry', href: '/terminology/bal-rating' },
        { name: 'Adelaide Hills home builders', href: '/home-builders-adelaide-hills' },
      ],
      buildpilotTake:
        'The CFS Bushfire Hazard Map is the most overlooked factor when buyers compare two Adelaide Hills blocks. A BAL-12.5 versus BAL-29 block can be a $20,000-$50,000 build cost difference on identical floor plans, before factoring in shutter and ember-screen design constraints.',
      jurisdiction: 'state',
      lastVerified: '2026-03-01',
    },
  ],

  'Industry Bodies': [
    {
      name: 'Housing Industry Association (HIA)',
      url: 'https://hia.com.au',
      summary: 'The national peak association for residential builders. BuildPilot cites HIA for industry data, the HIA-CSR South Australian Housing & Kitchen Awards, and the HIA cost guide series.',
      citedFor: [
        'HIA-CSR South Australian Housing & Kitchen Awards',
        'HIA Economics quarterly housing data',
        'HIA standard contract templates',
      ],
      internalLinks: [
        { name: 'About the BuildPilot Editorial Team', href: '/about/editorial-team' },
        { name: 'Make Choices lifecycle pillar', href: '/lifecycle/make-choices' },
      ],
      buildpilotTake:
        'The HIA standard contract is a starting point, not a finishing point. In our reading two clauses cause most contract surprises, the variations clause and the provisional sum reconciliation clause, both worth getting independent eyes on before signing.',
      jurisdiction: 'industry',
      lastVerified: '2026-03-01',
    },
    {
      name: 'Real Estate Institute of South Australia (REISA)',
      url: 'https://reisa.com.au',
      summary: 'The peak body for real estate agents in South Australia. BuildPilot cites REISA for Adelaide median price data, suburb-level growth trajectory analysis, and the regulatory context around the RLA300580 land agent licence held personally by BuildPilot founder George Giannakakis.',
      citedFor: [
        'Adelaide median price quarterly data',
        'Suburb growth trajectory commentary',
        'Land agent licensing context',
      ],
      internalLinks: [
        { name: 'About the BuildPilot Editorial Team', href: '/about/editorial-team' },
        { name: 'Adelaide suburb guides', href: '/south-australia/adelaide/areas' },
      ],
      buildpilotTake:
        'REISA quarterly median data is more current than ABS but covers fewer suburbs. Our default for Adelaide growth-corridor analysis is to cross-reference REISA quarterly with ABS Census demographics and ABS Building Approvals, then map all three against the BuildPilot suburb guide.',
      jurisdiction: 'industry',
      lastVerified: '2026-03-01',
    },
    {
      name: 'Australian Bureau of Statistics (ABS)',
      url: 'https://www.abs.gov.au',
      summary: 'The federal statistics agency. BuildPilot cites ABS for the quarterly Producer Price Index (construction inputs), Building Approvals dataset, and South Australian demographic trends underlying our growth-corridor analysis.',
      citedFor: [
        'Producer Price Index (construction materials inflation)',
        'Building Approvals data (SA)',
        'Census-based SA demographic trends',
      ],
      internalLinks: [
        { name: 'Adelaide Build Cost Report 2026', href: '/adelaide-build-cost-report-2026' },
        { name: 'Growth suburbs Adelaide', href: '/growth-suburbs-adelaide' },
      ],
      buildpilotTake:
        'The ABS Producer Price Index for construction is the leading indicator of where Adelaide build costs are heading 4-8 months ahead. Most builders update their inclusions list 6 months behind PPI, which is why mid-year contracts can get hit with material variations partway through the build.',
      jurisdiction: 'industry',
      lastVerified: '2026-03-01',
    },
  ],

  'Standards & Codes': [
    {
      name: 'National Construction Code (NCC) 2025',
      url: 'https://ncc.abcb.gov.au',
      summary: 'The legally binding minimum design and construction standard for every new home in Australia, published by the ABCB. The NCC 2025 edition applies to 2026 Adelaide builds approved on or after the adoption date.',
      citedFor: [
        '7-star NatHERS energy efficiency minimum',
        'Structural design requirements',
        'Plumbing and fire safety',
        'Accessibility (Livable Housing Design)',
      ],
      internalLinks: [
        { name: 'NCC terminology entry', href: '/terminology/ncc-national-construction-code' },
        { name: 'Energy-efficient home guide', href: '/energy-efficient-home-guide' },
      ],
      buildpilotTake:
        'The biggest 2025 change for Adelaide buyers is the Livable Housing accessibility requirements (wider doorways, reinforced bathroom walls, step-free entry). In our reading most builders are not yet pricing these into the base spec, expect a $4,000-$8,000 code compliance variation if your contract was drafted before NCC 2025 adoption.',
      jurisdiction: 'standards',
      lastVerified: '2026-03-01',
    },
    {
      name: 'AS 3959, Construction of buildings in bushfire-prone areas',
      url: 'https://www.standards.org.au',
      summary: 'Australian Standard governing bushfire-resilient construction. Mandates the BAL (Bushfire Attack Level) rating system referenced on every Adelaide Hills block before a development application can proceed.',
      citedFor: [
        'BAL-LOW through BAL-FZ rating definitions',
        'Construction requirements by BAL level',
      ],
      internalLinks: [
        { name: 'BAL Rating terminology entry', href: '/terminology/bal-rating' },
        { name: 'Adelaide Hills builders', href: '/home-builders-adelaide-hills' },
      ],
      buildpilotTake:
        'Our reading: BAL-12.5 adds modest cost (around $5,000-$10,000) and rarely changes the design. BAL-29 is where both cost and design constraints spike, over $20,000 extra plus shutter and ember-screen requirements. BAL-40 and BAL-FZ are where most Adelaide Hills buyers we work with reconsider whether a particular block is the right one.',
      jurisdiction: 'standards',
      lastVerified: '2026-03-01',
    },
    {
      name: 'NatHERS (Nationwide House Energy Rating Scheme)',
      url: 'https://www.nathers.gov.au',
      summary: 'The federally administered scheme measuring the thermal performance of new homes. The current 7-star minimum (from May 2024) is referenced across BuildPilot energy and cost content.',
      citedFor: [
        '7-star minimum rating (May 2024+)',
        'NatHERS-accredited assessor list',
        'Climate zone data for Adelaide builds',
      ],
      internalLinks: [
        { name: 'Energy-efficient home guide', href: '/energy-efficient-home-guide' },
        { name: 'NCC terminology entry', href: '/terminology/ncc-national-construction-code' },
      ],
      buildpilotTake:
        'The 7-star minimum is achievable on a standard Adelaide project home if orientation, eaves and glazing are sensible from the design stage. Where it gets expensive is north-facing blocks with poor solar access, west-facing glazing without shading, or eaveless modern facades, in our reading the cheapest path to 7 stars is the design stage, not the inclusions stage.',
      jurisdiction: 'standards',
      lastVerified: '2026-03-01',
    },
  ],
};

export const getSourceIndexTotals = () => {
  const sources = Object.values(SOURCE_INDEX).flat();
  const totalSources = sources.length;
  const totalGroups = Object.keys(SOURCE_INDEX).length;
  const totalCitedTopics = sources.reduce((s, src) => s + (src.citedFor || []).length, 0);
  const totalInternalLinks = sources.reduce((s, src) => s + (src.internalLinks || []).length, 0);
  return { totalSources, totalGroups, totalCitedTopics, totalInternalLinks };
};
